import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { IFormDisabilityData, IFormDisabilityState } from 'types/form';
import { IFormDisabilityProps } from './types';
import {
    $brightBlue,
    $deepBlue,
    $digitalBlack,
    SCBodyLarge,
    SCBodySmallItalic,
    SCContentDirectionWrapper,
    SCContentSectionResponsiveWrapper,
    SCContentSectionWrapper,
    SCContentWrapper,
    SCElementsWrapper,
    SCElementWrapper,
    SCErrorLabel,
    SCForm,
    SCFormNavigationWrapper,
    SCFormRefWrapper,
    SCFormTopNavWrapper,
    SCFormWrapper,
    SCSectionBody,
    SCSectionSubHead,
    SCSectionWrapper,
    SCSubHead,
    SCTempDateFieldWrapper
} from 'styles/global-styles';
import { TimelineState } from 'enums/TimeLine';
import {
    keysBorrowDetail,
    keysDisabilityDetailsHealthcare,
    labelMapDisability,
    labelMapIU
} from 'components/organisms/FormIU/fieldTypes';
import {
    validateDate,
    validateEmail,
    validatePhoneNumber,
    validateLast4SSN,
    validateZip,
    errorMapIU
} from 'utils/form/form';
import { FormNavigation } from 'components/molecules/FormNavigation';
import { TSBackNavigation } from 'components/molecules/TSBackNavigation';
import { TSFormProgressStepper } from 'components/molecules/TSFormProgressStepper';
import { TSTextField } from 'components/atoms/TSTextField';
import { TSMaskedTextField } from 'components/atoms/TSMaskedTextField';
import { TSAutocomplete } from 'components/atoms/TSAutocomplete';
import { TSRadioGroup } from 'components/atoms/TSRadioGroup';
import { BeforeUnload } from 'components/molecules/BeforeUnload';
import { stateOptionDict } from 'support/stateDictionary';
import { suffixOptions } from 'support/suffixDictionary';
import { MaxFieldLengths } from 'enums/MaxFieldLengths';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { SCChangeFormType } from './styles';
import { ClaimTypesEnum, TrackerEnum } from 'enums';
import { FlexAlignItemsEnum, FlexDirectionEnum, FlexJustifyContentEnum, FormFieldWidthEnum } from 'enums/StyleTypes';
import { Routes } from 'routes';
import { ClaimStateEnum } from 'enums/ClaimState';
import { TSDialog } from 'components/atoms/TSDialog';
import { TSButton } from 'components/atoms/TSButton';
import { TSButtonSizeEnum, TSButtonVariantsEnum } from 'enums/TSButtonVariants';
import {
    borrowerContactPreferenceRadioOptions,
    borrowerPaymentOptions,
    YesNoAutocompleteOptions,
    YesNoBooleanRadioOptions,
    YesNoRadioOptions
} from 'support/claimForm';
import { isPhoneIdentifierType } from 'utils/helpers/helpers';
import { ClaimOptionContext, IClaimOptionTypeState } from 'context/ClaimOption';
import {
    ClaimFormDisabilityContext,
    IClaimFormDisabilityType,
    IClaimFormDisabilityTypeState
} from 'context/ClaimFormDisability';
import { ClaimProgressDisabilityContext, IClaimProgressDisabilityTypeState } from 'context/ClaimProgressDisability';
import { DistributorDataContext, IDistributorDataTypeState } from 'context/DistributorData';
import { AuthenticationStateContext, IAuthenticationStateTypeState } from 'context/AuthenticationState';
import { editFormLabels } from 'support/formLabels';
import { TSDatePicker } from 'components/atoms/TSDatePicker';
import { disabilityCategories } from 'support/disabilityCategories';
import { TSRadioOrientationEnum, TSRadioSizeEnum } from 'enums/TSRadioVariants';
dayjs.extend(utc);
dayjs.extend(timezone);
const timeZone = 'America/Chicago';
const FormDisability = ({ children, changeFormType }: IFormDisabilityProps): ReactElement => {
    const fieldRef = useRef<HTMLInputElement>(null);
    const { contextStateClaimOption, updateContextStateClaimOption } = React.useContext(
        ClaimOptionContext
    ) as IClaimOptionTypeState;
    const { contextStateDistributorData, updateContextStateDistributorData } = React.useContext(
        DistributorDataContext
    ) as IDistributorDataTypeState;
    const {
        contextStateClaimFormDisability,
        updateContextStateClaimFormDisability,
        reinitialiseStateClaimFormDisability
    } = React.useContext(ClaimFormDisabilityContext) as IClaimFormDisabilityTypeState;
    const {
        contextStateClaimProgressDisability,
        updateContextStateClaimProgressDisability,
        resetContextStateClaimProgressDisability,
        claimProgressDisabilityCompletionStatus,
        updateClaimProgressDisabilityCompletionStatus,
        updateAllClaimProgressDisabilityCompletionStatus
    } = React.useContext(ClaimProgressDisabilityContext) as IClaimProgressDisabilityTypeState;
    const { contextStateAuthenticationState, updateContextStateAuthenticationState } = React.useContext(
        AuthenticationStateContext
    ) as IAuthenticationStateTypeState;
    const [emailInputError, setEmailInputError] = useState<string | null>(null);
    const [zipInputError, setZipInputError] = useState<string | null>(null);
    const [phoneNumberInputError, setPhoneNumberInputError] = useState<string | null>(null);
    const [ssnInputError, setSsnInputError] = useState<string | null>(null);
    const [dateOfBirthInputError, setDateOfBirthInputError] = useState<string | null>(null);
    const [dateOfIncidentError, setDateOfIncidentError] = useState<string | null>(null);
    const [dateOfLastWorkedError, setDateOfLastWorkedError] = useState<string | null>(null);
    const [dateOfPhysicianSeenError, setDateOfPhysicianSeenError] = useState<string | null>(null);
    const [dateExpectedWorking, setDateExpectedWorking] = useState<string | null>(null);
    const [healthcareZipInputError, setHealthcareZipInputError] = useState<string | null>(null);
    const [healthcarePhoneNumberInputError, setHealthcarePhoneNumberInputError] = useState<string | null>(null);
    const [employerZipInputError, setEmployerZipInputError] = useState<string | null>(null);
    const [employerPhoneNumberInputError, setEmployerPhoneNumberInputError] = useState<string | null>(null);
    const [employerEmailInputError, setEmployerEmailInputError] = useState<string | null>(null);
    const [submitterPhoneNumberInputError, setSubmitterPhoneNumberInputError] = useState<string | null>(null);
    const [submitterEmailInputError, setSubmitterEmailInputError] = useState<string | null>(null);
    const [ssnInputType, setSsnInputType] = useState<string>('text');
    const [onBehalfOf, setOnBehalfOf] = useState<boolean>(false);
    const [knowWhenExpectedWorking, setKnowWhenExpectedWorking] = React.useState<boolean>(false);
    const [formData, setFormData] = React.useState<IClaimFormDisabilityType | Record<string, unknown>>();
    const [isFormDirty, setIsFormDirty] = React.useState(false);
    const [formDeepCopy, setFormDeepCopy] = React.useState<IFormDisabilityData | Record<string, unknown>>();
    const [confirmModalIsVisible, setConfirmModalIsVisible] = useState(false);
    const [isPersonalInfoActive, setIsPersonalInfoActive] = useState(false);
    const [isDisabilityDetailsActive, setIsDisabilityDetailsActive] = useState(false);
    const [isEmployerInfoActive, setIsEmployerInfoActive] = useState(false);
    const [isHealthcareInfoActive, setIsHealthcareInfoActive] = useState(false);
    const [isCommentsActive, setIsCommentsActive] = useState(false);
    const [isClaimPaymentDetailsActive, setIsClaimPaymentDetailsActive] = useState(false);
    const [isBorrowerFirstAndLastPopulated, setIsBorrowerFirstAndLastPopulated] = useState(false);
    useEffect(() => {
        const contextStateClaimEditDeepCopy = structuredClone(formData);
        setFormDeepCopy(contextStateClaimEditDeepCopy);
    }, [formData]);
    useEffect(() => {
        formDeepCopy && JSON.stringify(formDeepCopy) !== JSON.stringify(formData) && setIsFormDirty(true);
    }, [formData, formDeepCopy]);
    const allowedRoutes = [
        `${window.location.origin}${Routes.CALLBACK}`,
        `${process.env.REACT_APP_IDP_ISSUER_URL}`,
        `${process.env.REACT_APP_IDP_ISSUER_URL}/default/v1/logout?id_token_hint`
    ];
    const stateOptions = Object.keys(stateOptionDict);
    const yesNoAutocompleteOptionValues = Object.keys(YesNoAutocompleteOptions);
    useEffect(() => {
        setKnowWhenExpectedWorking(formData?.disabilityKnowWhenExpectedWorking === 'yes');
    }, [formData?.disabilityKnowWhenExpectedWorking]);
    useEffect(() => {
        setOnBehalfOf(formData?.disabilityFiledOnBehalfOf === 'yes');
    }, [formData?.disabilityFiledOnBehalfOf]);
    const handleForm = (formEvent: React.FormEvent<HTMLInputElement>): void => {
        if (
            formEvent.currentTarget.id === 'borrowerMailingState' ||
            formEvent.currentTarget.id === 'disabilityHealthcareState' ||
            formEvent.currentTarget.id === 'employerState'
        ) {
            formEvent.currentTarget.value = formEvent.currentTarget.value.toUpperCase();
        }
        setFormData({
            ...formData,
            [formEvent.currentTarget.id]: formEvent.currentTarget.value
        });
        dispatchLastFieldChangedEvent(formEvent.currentTarget.id);
    };
    const handleEmailInput = (name: string, value: string): void => {
        if (name === 'borrowerEmail') {
            value.length > 0 && !validateEmail(value)
                ? setEmailInputError(errorMapIU['emailError'])
                : setEmailInputError(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        } else if (name === 'employerEmail') {
            value.length > 0 && !validateEmail(value)
                ? setEmployerEmailInputError(errorMapIU['emailError'])
                : setEmployerEmailInputError(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        } else if (name === 'submitterEmail') {
            value.length > 0 && !validateEmail(value)
                ? setSubmitterEmailInputError(errorMapIU['emailError'])
                : setSubmitterEmailInputError(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        }
    };
    const handleSSNInput = (name: string, value: string): void => {
        // Only allow numeric input
        const numericValue = value.replace(/[^0-9]/g, '');

        numericValue.length > 0 && !validateLast4SSN(numericValue)
            ? setSsnInputError(errorMapIU['ssnError'])
            : setSsnInputError(null);
        setFormData({
            ...formData,
            [name]: numericValue
        });
        dispatchLastFieldChangedEvent(name);
    };
    const handleDateInput = (name: string, value: string): void => {
        if (name === 'disabilityDateOfIncident') {
            value.length > 0 && !validateDate(value)
                ? setDateOfIncidentError(errorMapIU['dateError'])
                : setDateOfIncidentError(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        } else if (name === 'disabilityDateLastWorked') {
            value.length > 0 && !validateDate(value)
                ? setDateOfLastWorkedError(errorMapIU['dateError'])
                : setDateOfLastWorkedError(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        } else if (name === 'disabilityFirstPhysicianDate') {
            value.length > 0 && !validateDate(value)
                ? setDateOfPhysicianSeenError(errorMapIU['dateError'])
                : setDateOfPhysicianSeenError(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        } else if (name === 'disabilityDateExpectedWorking') {
            value.length > 0 && !validateDate(value)
                ? setDateExpectedWorking(errorMapIU['dateError'])
                : setDateExpectedWorking(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        } else if (name === 'borrowerDateOfBirth') {
            value.length > 0 && !validateDate(value)
                ? setDateOfBirthInputError(errorMapIU['dateError'])
                : setDateOfBirthInputError(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        }
    };
    const handleZIPInput = (name: string, value: string): void => {
        if (name === 'borrowerMailingZip') {
            value.length > 0 && !validateZip(value) ? setZipInputError(errorMapIU['zipError']) : setZipInputError(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        } else if (name === 'employerZip') {
            value.length > 0 && !validateZip(value)
                ? setEmployerZipInputError(errorMapIU['zipError'])
                : setEmployerZipInputError(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        } else if (name === 'disabilityHealthcareZip') {
            value.length > 0 && !validateZip(value)
                ? setHealthcareZipInputError(errorMapIU['zipError'])
                : setHealthcareZipInputError(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        }
    };
    const handlePhoneNumberInput = (name: string, value: string): void => {
        if (name === 'borrowerPhone') {
            value.length > 0 && !validatePhoneNumber(value)
                ? setPhoneNumberInputError(errorMapIU['phoneError'])
                : setPhoneNumberInputError(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        } else if (name === 'employerContactNumber') {
            value.length > 0 && !validatePhoneNumber(value)
                ? setEmployerPhoneNumberInputError(errorMapIU['phoneError'])
                : setEmployerPhoneNumberInputError(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        } else if (name === 'disabilityHealthcareContactNumber') {
            value.length > 0 && !validatePhoneNumber(value)
                ? setHealthcarePhoneNumberInputError(errorMapIU['phoneError'])
                : setHealthcarePhoneNumberInputError(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        } else if (name === 'submitterPhone') {
            value.length > 0 && !validatePhoneNumber(value)
                ? setSubmitterPhoneNumberInputError(errorMapIU['phoneError'])
                : setSubmitterPhoneNumberInputError(null);
            setFormData({
                ...formData,
                [name]: value
            });
            dispatchLastFieldChangedEvent(name);
        }
    };
    const handleRadioInput = (
        name: string,
        radioValue: string | boolean,
        dependentPropertiesToReset: string[] = []
    ): void => {
        setFormData({
            ...formData,
            [name]: radioValue,
            ...Object.fromEntries(dependentPropertiesToReset.map((prop) => [prop, null]))
        });
        dispatchLastFieldChangedEvent(name);
    };
    const handleAutocompleteInput = (name: string, value: string, dependentPropertiesToReset: string[] = []): void => {
        setFormData({
            ...formData,
            [name]: value,
            ...Object.fromEntries(dependentPropertiesToReset.map((prop) => [prop, null]))
        });
        dispatchLastFieldChangedEvent(name);
    };
    const handleSaveFormState = (event: React.FormEvent, formData: IFormDisabilityData | any): void => {
        // saveFormDisabilityState(formData);
        updateContextStateClaimFormDisability(formData);
    };
    const dispatchLastFieldChangedEvent = (nameOfLastFieldChanged: string): void => {
        document.dispatchEvent(
            new CustomEvent(TrackerEnum.LAST_CLAIM_FIELD_CHANGED, {
                detail: {
                    claimType: ClaimTypesEnum.Disability,
                    distributorName: contextStateDistributorData.distributorName,
                    lastClaimFieldChanged: nameOfLastFieldChanged
                }
            })
        );
    };
    const dispatchClaimProgressEvent = (nameOfClaimStep: string): void => {
        document.dispatchEvent(
            new CustomEvent(TrackerEnum.PAYMENT_GUARD_CLAIMPROCESS, {
                detail: {
                    claimType: ClaimTypesEnum.Disability,
                    distributorName: contextStateDistributorData.distributorName,
                    id: nameOfClaimStep
                }
            })
        );
    };
    const dispatchBorrowerNameChangedEvent = (isBorrowerFirstAndLastPopulated: boolean): void => {
        document.dispatchEvent(
            new CustomEvent(TrackerEnum.BORROWER_NAME_CHANGED, {
                detail: {
                    claimType: ClaimTypesEnum.Disability,
                    distributorName: contextStateDistributorData.distributorName,
                    isBorrowerFirstLastNamePopulated: isBorrowerFirstAndLastPopulated
                }
            })
        );
    };
    useEffect(() => {
        const comboBoxButtons = fieldRef.current?.getElementsByClassName('Combobox__menu-toggle');
        if (typeof comboBoxButtons !== 'undefined') {
            Array.from(comboBoxButtons).forEach((el) => el.setAttribute('type', 'button'));
        }
    }, [contextStateClaimProgressDisability, formData]);
    useEffect(() => {
        if (fieldRef.current && contextStateClaimProgressDisability[0].status !== TimelineState.ACTIVE) {
            fieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setIsPersonalInfoActive(contextStateClaimProgressDisability[0].status === TimelineState.ACTIVE);
        setIsDisabilityDetailsActive(contextStateClaimProgressDisability[1].status === TimelineState.ACTIVE);
        setIsEmployerInfoActive(contextStateClaimProgressDisability[2].status === TimelineState.ACTIVE);
        setIsHealthcareInfoActive(contextStateClaimProgressDisability[3].status === TimelineState.ACTIVE);
        setIsCommentsActive(contextStateClaimProgressDisability[4].status === TimelineState.ACTIVE);
        setIsClaimPaymentDetailsActive(contextStateClaimProgressDisability[5].status === TimelineState.ACTIVE);
    }, [contextStateClaimProgressDisability]);
    useEffect(() => {
        if (isPersonalInfoActive) {
            dispatchClaimProgressEvent(TrackerEnum.CLAIMSTEP_PERSONAL_INFO);
        }
    }, [isPersonalInfoActive]);
    useEffect(() => {
        if (isDisabilityDetailsActive) {
            dispatchClaimProgressEvent(TrackerEnum.CLAIMSTEP_DISABILITY_DETAILS);
        }
    }, [isDisabilityDetailsActive]);
    useEffect(() => {
        if (isEmployerInfoActive) {
            dispatchClaimProgressEvent(TrackerEnum.CLAIMSTEP_EMPLOYER_INFO);
        }
    }, [isEmployerInfoActive]);
    useEffect(() => {
        if (isHealthcareInfoActive) {
            dispatchClaimProgressEvent(TrackerEnum.CLAIMSTEP_HEALTHCARE_INFO);
        }
    }, [isHealthcareInfoActive]);
    useEffect(() => {
        if (isCommentsActive) {
            dispatchClaimProgressEvent(TrackerEnum.CLAIMSTEP_COMMENTS);
        }
    }, [isCommentsActive]);
    useEffect(() => {
        if (isClaimPaymentDetailsActive) {
            dispatchClaimProgressEvent(TrackerEnum.CLAIMSTEP_CLAIM_PAYMENT_DETAILS);
        }
    }, [isClaimPaymentDetailsActive]);
    useEffect(() => {
        if (formData?.borrowerNameFirst && formData?.borrowerNameLast) {
            setIsBorrowerFirstAndLastPopulated(true);
        } else {
            setIsBorrowerFirstAndLastPopulated(false);
        }
    }, [formData?.borrowerNameFirst, formData?.borrowerNameLast]);
    useEffect(() => {
        dispatchBorrowerNameChangedEvent(isBorrowerFirstAndLastPopulated);
    }, [isBorrowerFirstAndLastPopulated]);
    useEffect(() => {
        const formStatusAllComplete = contextStateClaimProgressDisability.some(
            (obj) => obj.status === TimelineState.COMPLETE
        );
        if (formStatusAllComplete) {
            return;
        } else {
            updateContextStateClaimProgressDisability('id-1', TimelineState.ACTIVE);
        }
    }, []);
    useEffect(() => {
        setFormData(contextStateClaimFormDisability);
    }, []);
    // console.info(
    //     '\n::::::::::::::::::::::::::::::::FormDisability:::::::::::::::::::::::::::::::::',
    //     '\n::isPhoneIdentifierType::',
    //     isPhoneIdentifierType,
    //     '\n::formData::',
    //     formData,
    //     '\n::contextStateClaimProgressDisability::',
    //     contextStateClaimProgressDisability,
    //     '\n::isFormDirty::',
    //     isFormDirty,
    //     '\n:::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::'
    // );

    return (
        <SCContentWrapper>
            <SCContentDirectionWrapper
                flexDirection={FlexDirectionEnum.COLUMN}
                flexAlignment={FlexAlignItemsEnum.FLEX_START}
            >
                <SCContentSectionResponsiveWrapper
                    flexDirection={FlexDirectionEnum.COLUMN}
                    flexAlignment={FlexAlignItemsEnum.FLEX_START}
                    flexGap={'24px'}
                    sectionWidth={'100%'}
                >
                    <SCFormRefWrapper ref={fieldRef}>
                        <SCFormTopNavWrapper>
                            <TSBackNavigation isFormDirty={isFormDirty} />
                            <TSFormProgressStepper />
                        </SCFormTopNavWrapper>
                        <SCSubHead textColor={$deepBlue}>File a Claim: Disability</SCSubHead>
                        {contextStateClaimProgressDisability[0].status === TimelineState.ACTIVE && (
                            <SCSectionWrapper>
                                {contextStateDistributorData?.client_content?.claim_options.find(
                                    (x) => x.value === ClaimTypesEnum.Jobloss
                                ) && (
                                    <SCChangeFormType
                                        onClick={(): void => {
                                            isFormDirty
                                                ? setConfirmModalIsVisible(true)
                                                : changeFormType && changeFormType();
                                            document.dispatchEvent(
                                                new CustomEvent(TrackerEnum.PAYMENT_GUARD_CLAIMPROCESS, {
                                                    detail: {
                                                        claimType: ClaimTypesEnum.Jobloss,
                                                        distributorName: contextStateDistributorData.distributorName,
                                                        id: TrackerEnum.CLAIMSTEP_START
                                                    }
                                                })
                                            );
                                        }}
                                    >
                                        Do you need to file a job loss claim instead? Click here.
                                    </SCChangeFormType>
                                )}
                                <SCBodyLarge>
                                    Please enter in the following information so we can better understand your
                                    situation.
                                </SCBodyLarge>
                                <SCBodyLarge>All fields are required unless otherwise indicated.</SCBodyLarge>
                                <SCFormWrapper>
                                    <form
                                        id={'borrower'}
                                        onSubmit={(e: React.FormEvent<HTMLFormElement>): void =>
                                            handleSaveFormState(e, formData)
                                        }
                                        autoComplete={'on'}
                                        className={SCForm}
                                    >
                                        <SCSectionSubHead>Personal Information</SCSectionSubHead>
                                        <SCElementsWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSTextField
                                                    id={'borrowerNameFirst'}
                                                    name={'fname'}
                                                    value={formData?.borrowerNameFirst as string}
                                                    label={labelMapDisability['borrowerNameFirst']}
                                                    placeholder={`Enter ${labelMapDisability['borrowerNameFirst']}`}
                                                    ariaLabel={labelMapDisability['borrowerNameFirst']}
                                                    maxLength={MaxFieldLengths.FIRST_NAME}
                                                    onChange={handleForm}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSTextField
                                                    id={'borrowerNameMiddle'}
                                                    name={'mname'}
                                                    value={formData?.borrowerNameMiddle as string}
                                                    label={labelMapDisability['borrowerNameMiddle']}
                                                    placeholder={`Enter ${labelMapDisability['borrowerNameMiddle']}`}
                                                    ariaLabel={labelMapDisability['borrowerNameMiddle']}
                                                    maxLength={MaxFieldLengths.NAME}
                                                    onChange={handleForm}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSTextField
                                                    id={'borrowerNameLast'}
                                                    name={'lname'}
                                                    value={formData?.borrowerNameLast as string}
                                                    label={labelMapDisability['borrowerNameLast']}
                                                    placeholder={`Enter ${labelMapDisability['borrowerNameLast']}`}
                                                    ariaLabel={labelMapDisability['borrowerNameLast']}
                                                    maxLength={MaxFieldLengths.LAST_NAME}
                                                    onChange={handleForm}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSAutocomplete
                                                    id={'borrowerSuffix'}
                                                    name={'suffix'}
                                                    value={
                                                        suffixOptions.find(
                                                            (opt) => opt.id === formData?.borrowerSuffix
                                                        ) ?? null
                                                    }
                                                    label={labelMapDisability['borrowerSuffix']}
                                                    placeholder={`Select ${labelMapDisability['borrowerSuffix']}`}
                                                    ariaLabel={labelMapDisability['borrowerSuffix']}
                                                    options={suffixOptions}
                                                    getOptionLabel={(option): string => option?.label ?? ''}
                                                    onChange={(event, value, reason): void => {
                                                        handleAutocompleteInput('borrowerSuffix', value?.id ?? null);
                                                    }}
                                                ></TSAutocomplete>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSDatePicker
                                                    id={'borrowerDateOfBirth'}
                                                    value={
                                                        formData?.borrowerDateOfBirth &&
                                                        typeof formData.borrowerDateOfBirth === 'string' &&
                                                        formData.borrowerDateOfBirth.length > 0
                                                            ? dayjs(formData.borrowerDateOfBirth)
                                                            : null
                                                    }
                                                    label={labelMapDisability['borrowerDateOfBirth']}
                                                    placeholder={`Enter ${labelMapDisability['borrowerDateOfBirth']}`}
                                                    ariaLabel={labelMapDisability['borrowerDateOfBirth']}
                                                    onChange={(value: Dayjs | null): void => {
                                                        handleDateInput(
                                                            'borrowerDateOfBirth',
                                                            value && dayjs(value).isValid()
                                                                ? dayjs(value).format('MM/DD/YYYY')
                                                                : ''
                                                        );
                                                    }}
                                                />
                                                <SCErrorLabel>{dateOfBirthInputError}</SCErrorLabel>
                                            </SCElementWrapper>
                                        </SCElementsWrapper>
                                        {!isPhoneIdentifierType(
                                            contextStateDistributorData.borrowerIdentifierType as number
                                        ) && (
                                            <>
                                                <SCElementsWrapper>
                                                    <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                        We use this to find your policy
                                                    </SCElementWrapper>
                                                </SCElementsWrapper>
                                                <SCElementsWrapper>
                                                    <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                        <TSTextField
                                                            id={'borrowerSSN'}
                                                            name={'ssn'}
                                                            type={ssnInputType}
                                                            value={formData?.borrowerSSN as string}
                                                            label={labelMapDisability['borrowerSSN']}
                                                            placeholder={`Enter ${labelMapDisability['borrowerSSN']}`}
                                                            ariaLabel={labelMapDisability['borrowerSSN']}
                                                            maxLength={MaxFieldLengths.SSN}
                                                            onBlur={(): void => {
                                                                setSsnInputType('password');
                                                            }}
                                                            onChange={(
                                                                formEvent: React.FormEvent<HTMLInputElement>
                                                            ): void => {
                                                                handleSSNInput(
                                                                    'borrowerSSN',
                                                                    formEvent.currentTarget.value
                                                                );
                                                            }}
                                                            onFocus={(): void => {
                                                                setSsnInputType('text');
                                                            }}
                                                        ></TSTextField>
                                                        <SCErrorLabel>{ssnInputError}</SCErrorLabel>
                                                    </SCElementWrapper>
                                                    <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                        We need this to find your policy
                                                    </SCElementWrapper>
                                                </SCElementsWrapper>
                                            </>
                                        )}
                                        <SCElementsWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSTextField
                                                    id={'borrowerEmail'}
                                                    name={'email'}
                                                    type={'email'}
                                                    value={formData?.borrowerEmail as string}
                                                    label={'Email Address'}
                                                    placeholder={`Enter ${labelMapDisability['borrowerEmail']}`}
                                                    ariaLabel={labelMapDisability['borrowerEmail']}
                                                    maxLength={MaxFieldLengths.EMAIL}
                                                    onChange={(formEvent: React.FormEvent<HTMLInputElement>): void => {
                                                        handleEmailInput(
                                                            'borrowerEmail',
                                                            formEvent.currentTarget.value
                                                        );
                                                    }}
                                                ></TSTextField>
                                                <SCErrorLabel>{emailInputError}</SCErrorLabel>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSMaskedTextField
                                                    id={'borrowerPhone'}
                                                    name={'phone'}
                                                    fieldType={'phone'}
                                                    value={formData?.borrowerPhone as string}
                                                    label={'Phone Number'}
                                                    placeholder={`Enter ${labelMapDisability['borrowerPhone']}`}
                                                    ariaLabel={labelMapDisability['borrowerPhone']}
                                                    onChange={(event): void => {
                                                        handlePhoneNumberInput('borrowerPhone', event.target.value);
                                                    }}
                                                ></TSMaskedTextField>
                                                <SCErrorLabel>{phoneNumberInputError}</SCErrorLabel>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSRadioGroup
                                                    id={'borrowerContactPreference'}
                                                    value={formData?.borrowerContactPreference}
                                                    radioGroupLabel={labelMapDisability['borrowerContactPreference']}
                                                    radioGroupValues={borrowerContactPreferenceRadioOptions}
                                                    onChange={(event): void => {
                                                        handleRadioInput(
                                                            'borrowerContactPreference',
                                                            event.target.value
                                                        );
                                                    }}
                                                ></TSRadioGroup>
                                            </SCElementWrapper>
                                        </SCElementsWrapper>
                                        <SCSectionSubHead>Mailing Address</SCSectionSubHead>
                                        <SCElementsWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSTextField
                                                    id={'borrowerMailingAddress'}
                                                    name={'address1'}
                                                    value={formData?.borrowerMailingAddress as string}
                                                    label={labelMapDisability['borrowerMailingAddress']}
                                                    placeholder={`Enter ${labelMapDisability['borrowerMailingAddress']}`}
                                                    ariaLabel={labelMapDisability['borrowerMailingAddress']}
                                                    maxLength={MaxFieldLengths.ADDRESS}
                                                    onChange={handleForm}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSTextField
                                                    id={'borrowerMailingAddress2'}
                                                    name={'address2'}
                                                    value={formData?.borrowerMailingAddress2 as string}
                                                    label={labelMapDisability['borrowerMailingAddress2']}
                                                    placeholder={`Enter ${labelMapDisability['borrowerMailingAddress2']}`}
                                                    ariaLabel={labelMapDisability['borrowerMailingAddress2']}
                                                    maxLength={MaxFieldLengths.ADDRESS}
                                                    onChange={handleForm}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSTextField
                                                    id={'borrowerMailingCity'}
                                                    name={'city'}
                                                    value={formData?.borrowerMailingCity as string}
                                                    label={labelMapDisability['borrowerMailingCity']}
                                                    placeholder={`Enter ${labelMapDisability['borrowerMailingCity']}`}
                                                    ariaLabel={labelMapDisability['borrowerMailingCity']}
                                                    maxLength={MaxFieldLengths.CITY}
                                                    onChange={handleForm}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSAutocomplete
                                                    id={'borrowerMailingState'}
                                                    name={'state'}
                                                    value={formData?.borrowerMailingState}
                                                    label={labelMapDisability['borrowerMailingState']}
                                                    placeholder={`Select ${labelMapDisability['borrowerMailingState']}`}
                                                    ariaLabel={labelMapDisability['borrowerMailingState']}
                                                    options={stateOptions}
                                                    getOptionLabel={(stateAbbreviation): string =>
                                                        stateOptionDict[stateAbbreviation]
                                                    }
                                                    onChange={(event, value, reason): void => {
                                                        handleAutocompleteInput('borrowerMailingState', value);
                                                    }}
                                                ></TSAutocomplete>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSMaskedTextField
                                                    id={'borrowerMailingZip'}
                                                    name={'zip'}
                                                    fieldType={'zipCode'}
                                                    value={formData?.borrowerMailingZip as string}
                                                    label={labelMapDisability['borrowerMailingZip']}
                                                    placeholder={`Enter ${labelMapDisability['borrowerMailingZip']}`}
                                                    ariaLabel={labelMapDisability['borrowerMailingZip']}
                                                    onChange={(event): void => {
                                                        handleZIPInput('borrowerMailingZip', event.target.value);
                                                    }}
                                                ></TSMaskedTextField>
                                                <SCErrorLabel>{zipInputError}</SCErrorLabel>
                                            </SCElementWrapper>
                                        </SCElementsWrapper>
                                        <SCFormNavigationWrapper>
                                            {contextStateClaimProgressDisability.some(
                                                (e) => e.status === TimelineState.ACTIVE
                                            ) && (
                                                <FormNavigation
                                                    formData={formData}
                                                    isFormDirty={isFormDirty}
                                                    requiredFields={
                                                        !isPhoneIdentifierType(
                                                            contextStateDistributorData.borrowerIdentifierType as number
                                                        )
                                                            ? keysBorrowDetail
                                                            : [
                                                                  'borrowerNameFirst',
                                                                  'borrowerNameLast',
                                                                  'borrowerEmail',
                                                                  'borrowerPhone',
                                                                  'borrowerMailingAddress',
                                                                  'borrowerMailingCity',
                                                                  'borrowerMailingState',
                                                                  'borrowerMailingZip',
                                                                  'borrowerDateOfBirth'
                                                              ]
                                                    }
                                                    validationFields={[
                                                        !emailInputError,
                                                        !ssnInputError,
                                                        !phoneNumberInputError,
                                                        !zipInputError,
                                                        !dateOfBirthInputError
                                                    ]}
                                                />
                                            )}
                                        </SCFormNavigationWrapper>
                                    </form>
                                </SCFormWrapper>
                            </SCSectionWrapper>
                        )}
                        {isDisabilityDetailsActive && (
                            <SCSectionWrapper>
                                <SCBodyLarge>
                                    We’ve got your personal details saved. Next, please enter in the following
                                    disability details so we can better understand your situation.
                                </SCBodyLarge>
                                <SCBodyLarge>All fields are required unless otherwise indicated.</SCBodyLarge>
                                <SCFormWrapper>
                                    <form
                                        id={'borrower'}
                                        onSubmit={(e: React.FormEvent<HTMLFormElement>): void =>
                                            handleSaveFormState(e, formData)
                                        }
                                        autoComplete={'on'}
                                    >
                                        <SCSectionSubHead>Disability Details</SCSectionSubHead>
                                        <SCElementsWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSDatePicker
                                                    id={'disabilityDateOfIncident'}
                                                    value={
                                                        formData?.disabilityDateOfIncident &&
                                                        typeof formData.disabilityDateOfIncident === 'string' &&
                                                        formData.disabilityDateOfIncident.length > 0
                                                            ? dayjs(formData.disabilityDateOfIncident)
                                                            : null
                                                    }
                                                    label={labelMapDisability['disabilityDateOfIncident']}
                                                    placeholder={`Enter ${labelMapDisability['disabilityDateOfIncident']}`}
                                                    ariaLabel={labelMapDisability['disabilityDateOfIncident']}
                                                    onChange={(value: Dayjs | null): void => {
                                                        handleDateInput(
                                                            'disabilityDateOfIncident',
                                                            value && dayjs(value).isValid()
                                                                ? dayjs(value).format('MM/DD/YYYY')
                                                                : ''
                                                        );
                                                    }}
                                                />
                                                <SCErrorLabel>{dateOfIncidentError}</SCErrorLabel>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSDatePicker
                                                    id={'disabilityDateLastWorked'}
                                                    value={
                                                        formData?.disabilityDateLastWorked &&
                                                        typeof formData.disabilityDateLastWorked === 'string' &&
                                                        formData.disabilityDateLastWorked.length > 0
                                                            ? dayjs(formData.disabilityDateLastWorked)
                                                            : null
                                                    }
                                                    label={labelMapDisability['disabilityDateLastWorked']}
                                                    placeholder={`Enter ${labelMapDisability['disabilityDateLastWorked']}`}
                                                    ariaLabel={labelMapDisability['disabilityDateLastWorked']}
                                                    onChange={(value: Dayjs | null): void => {
                                                        handleDateInput(
                                                            'disabilityDateLastWorked',
                                                            value && dayjs(value).isValid()
                                                                ? dayjs(value).format('MM/DD/YYYY')
                                                                : ''
                                                        );
                                                    }}
                                                />
                                                <SCErrorLabel>{dateOfLastWorkedError}</SCErrorLabel>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSRadioGroup
                                                    id={'disabilityKnowWhenExpectedWorking'}
                                                    value={formData?.disabilityKnowWhenExpectedWorking}
                                                    radioGroupLabel={
                                                        labelMapDisability['disabilityKnowWhenExpectedWorking']
                                                    }
                                                    radioGroupValues={YesNoRadioOptions}
                                                    onChange={(event): void => {
                                                        handleRadioInput(
                                                            'disabilityKnowWhenExpectedWorking',
                                                            event.target.value,
                                                            ['disabilityDateExpectedWorking']
                                                        );
                                                    }}
                                                ></TSRadioGroup>
                                            </SCElementWrapper>
                                            {knowWhenExpectedWorking && (
                                                <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                    <TSDatePicker
                                                        id={'disabilityDateExpectedWorking'}
                                                        value={
                                                            formData?.disabilityDateExpectedWorking &&
                                                            typeof formData.disabilityDateExpectedWorking ===
                                                                'string' &&
                                                            formData.disabilityDateExpectedWorking.length > 0
                                                                ? dayjs(formData.disabilityDateExpectedWorking)
                                                                : null
                                                        }
                                                        label={labelMapDisability['disabilityDateExpectedWorking']}
                                                        placeholder={`Enter ${labelMapDisability['disabilityDateExpectedWorking']}`}
                                                        ariaLabel={labelMapDisability['disabilityDateExpectedWorking']}
                                                        onChange={(value: Dayjs | null): void => {
                                                            handleDateInput(
                                                                'disabilityDateExpectedWorking',
                                                                value && dayjs(value).isValid()
                                                                    ? dayjs(value).format('MM/DD/YYYY')
                                                                    : ''
                                                            );
                                                        }}
                                                    />
                                                    <SCErrorLabel>{dateExpectedWorking}</SCErrorLabel>
                                                </SCElementWrapper>
                                            )}
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.TRIPLE}>
                                                <TSTextField
                                                    id={'disabilityExplanationOf'}
                                                    value={formData?.disabilityExplanationOf as string}
                                                    label={labelMapDisability['disabilityExplanationOf']}
                                                    placeholder={`Enter an explanation of your disability incident...`}
                                                    ariaLabel={labelMapDisability['disabilityExplanationOf']}
                                                    maxLength={MaxFieldLengths.GENERAL_MAX}
                                                    multiline={true}
                                                    minRows={5}
                                                    onChange={(event): void => {
                                                        handleForm(event as any);
                                                    }}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSAutocomplete
                                                    id={'disabilityCategory'}
                                                    name={'suffix'}
                                                    value={
                                                        disabilityCategories.find(
                                                            (opt) => opt.id === formData?.disabilityCategory
                                                        ) ?? null
                                                    }
                                                    label={labelMapDisability['disabilityCategory']}
                                                    placeholder={`Select ${labelMapDisability['disabilityCategory']}`}
                                                    ariaLabel={labelMapDisability['disabilityCategory']}
                                                    options={disabilityCategories}
                                                    getOptionLabel={(option): string => option?.label ?? ''}
                                                    onChange={(event, value, reason): void => {
                                                        handleAutocompleteInput(
                                                            'disabilityCategory',
                                                            value?.id ?? null
                                                        );
                                                    }}
                                                ></TSAutocomplete>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.TRIPLE}>
                                                <TSTextField
                                                    id={'disabilityTypeOfSurgery'}
                                                    value={formData?.disabilityTypeOfSurgery as string}
                                                    label={labelMapDisability['disabilityTypeOfSurgery']}
                                                    placeholder={`Enter an explanation of any resulting surgical procedures...`}
                                                    ariaLabel={labelMapDisability['disabilityTypeOfSurgery']}
                                                    maxLength={MaxFieldLengths.DISABILITY_TYPE_OF_SURGERY}
                                                    multiline={true}
                                                    minRows={5}
                                                    onChange={handleForm}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSDatePicker
                                                    id={'disabilityFirstPhysicianDate'}
                                                    value={
                                                        formData?.disabilityFirstPhysicianDate &&
                                                        typeof formData.disabilityFirstPhysicianDate === 'string' &&
                                                        formData.disabilityFirstPhysicianDate.length > 0
                                                            ? dayjs(formData.disabilityFirstPhysicianDate)
                                                            : null
                                                    }
                                                    label={labelMapDisability['disabilityFirstPhysicianDate']}
                                                    placeholder={`Enter ${labelMapDisability['disabilityFirstPhysicianDate']}`}
                                                    ariaLabel={labelMapDisability['disabilityFirstPhysicianDate']}
                                                    onChange={(value: Dayjs | null): void => {
                                                        handleDateInput(
                                                            'disabilityFirstPhysicianDate',
                                                            value && dayjs(value).isValid()
                                                                ? dayjs(value).format('MM/DD/YYYY')
                                                                : ''
                                                        );
                                                    }}
                                                />
                                                <SCErrorLabel>{dateOfPhysicianSeenError}</SCErrorLabel>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.TRIPLE}>
                                                <TSTextField
                                                    id={'disabilityJobDuties'}
                                                    value={formData?.disabilityJobDuties as string}
                                                    label={labelMapDisability['disabilityJobDuties']}
                                                    placeholder={`Enter ${labelMapDisability['disabilityJobDuties']}`}
                                                    ariaLabel={labelMapDisability['disabilityJobDuties']}
                                                    maxLength={MaxFieldLengths.GENERAL_MAX}
                                                    multiline={true}
                                                    minRows={5}
                                                    onChange={(event): void => {
                                                        handleForm(event as any);
                                                    }}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                        </SCElementsWrapper>
                                        <SCFormNavigationWrapper>
                                            {contextStateClaimProgressDisability.some(
                                                (e) => e.status === TimelineState.ACTIVE
                                            ) && (
                                                <FormNavigation
                                                    formData={formData}
                                                    requiredFields={[
                                                        'disabilityExplanationOf',
                                                        'disabilityKnowWhenExpectedWorking',
                                                        'disabilityDateLastWorked',
                                                        'disabilityDateOfIncident',
                                                        'disabilityFirstPhysicianDate'
                                                    ]}
                                                    validationFields={[
                                                        !dateOfIncidentError,
                                                        !dateOfLastWorkedError,
                                                        !dateOfPhysicianSeenError
                                                    ]}
                                                />
                                            )}
                                        </SCFormNavigationWrapper>
                                    </form>
                                </SCFormWrapper>
                            </SCSectionWrapper>
                        )}
                        {isEmployerInfoActive && (
                            <SCSectionWrapper>
                                <SCBodyLarge>
                                    Great, we’ve got your claim details recorded. Next, please provide us with your
                                    employer’s contact information (for example, the corporate office, a manager, or the
                                    HR department.) If we need to contact them during the claim review, we will notify
                                    you to obtain your consent to do so.
                                </SCBodyLarge>
                                <SCBodyLarge>All fields are required unless otherwise indicated.</SCBodyLarge>
                                <SCFormWrapper>
                                    <form
                                        id={'borrower'}
                                        onSubmit={(e: React.FormEvent<HTMLFormElement>): void =>
                                            handleSaveFormState(e, formData)
                                        }
                                        autoComplete={'on'}
                                    >
                                        <SCElementWrapper>
                                            <SCSectionSubHead>Employer Details</SCSectionSubHead>
                                            <SCBodyLarge>
                                                We may need to obtain information from your employer to confirm your
                                                employment information.
                                            </SCBodyLarge>
                                        </SCElementWrapper>
                                        <SCElementsWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.SINGLE}>
                                                <TSTextField
                                                    id={'employerName'}
                                                    value={formData?.employerName as string}
                                                    label={labelMapDisability['employerName']}
                                                    placeholder={`Enter ${labelMapDisability['employerName']}`}
                                                    ariaLabel={labelMapDisability['employerName']}
                                                    maxLength={MaxFieldLengths.COMPANY_NAME}
                                                    onChange={handleForm}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.SINGLE}>
                                                <TSTextField
                                                    id={'employerEmail'}
                                                    type={'email'}
                                                    value={formData?.employerEmail as string}
                                                    label={labelMapDisability['employerEmail']}
                                                    placeholder={`Enter ${labelMapDisability['employerEmail']}`}
                                                    ariaLabel={labelMapDisability['employerEmail']}
                                                    maxLength={MaxFieldLengths.EMAIL}
                                                    onChange={(formEvent: React.FormEvent<HTMLInputElement>): void => {
                                                        handleEmailInput(
                                                            'employerEmail',
                                                            formEvent.currentTarget.value
                                                        );
                                                    }}
                                                ></TSTextField>
                                                <SCErrorLabel>{employerEmailInputError}</SCErrorLabel>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.SINGLE}>
                                                <TSMaskedTextField
                                                    id={'employerContactNumber'}
                                                    fieldType={'phone'}
                                                    value={formData?.employerContactNumber as string}
                                                    label={labelMapDisability['employerContactNumber']}
                                                    placeholder={`Enter ${labelMapDisability['employerContactNumber']}`}
                                                    ariaLabel={labelMapDisability['employerContactNumber']}
                                                    onChange={(event): void => {
                                                        handlePhoneNumberInput(
                                                            'employerContactNumber',
                                                            event.target.value
                                                        );
                                                    }}
                                                ></TSMaskedTextField>
                                                <SCErrorLabel>{employerPhoneNumberInputError}</SCErrorLabel>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.TRIPLE}>
                                                <TSTextField
                                                    id={'employerAddress'}
                                                    value={formData?.employerAddress as string}
                                                    label={labelMapDisability['employerAddress']}
                                                    placeholder={`Enter ${labelMapDisability['employerAddress']}`}
                                                    ariaLabel={labelMapDisability['employerAddress']}
                                                    maxLength={MaxFieldLengths.ADDRESS}
                                                    onChange={handleForm}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                        </SCElementsWrapper>
                                        <SCElementsWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.SINGLE}>
                                                <TSTextField
                                                    id={'employerCity'}
                                                    value={formData?.employerCity as string}
                                                    label={labelMapDisability['employerCity']}
                                                    placeholder={`Enter ${labelMapDisability['employerCity']}`}
                                                    ariaLabel={labelMapDisability['employerCity']}
                                                    maxLength={MaxFieldLengths.CITY}
                                                    onChange={handleForm}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.SINGLE}>
                                                <TSAutocomplete
                                                    id={'employerState'}
                                                    value={formData?.employerState}
                                                    label={labelMapDisability['employerState']}
                                                    options={stateOptions}
                                                    getOptionLabel={(stateAbbreviation): string =>
                                                        stateOptionDict[stateAbbreviation]
                                                    }
                                                    onChange={(event, value): void => {
                                                        handleAutocompleteInput('employerState', value);
                                                    }}
                                                ></TSAutocomplete>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.SINGLE}>
                                                <TSMaskedTextField
                                                    id={'employerZip'}
                                                    fieldType={'zipCode'}
                                                    value={formData?.employerZip as string}
                                                    label={labelMapDisability['employerZip']}
                                                    placeholder={`Enter ${labelMapDisability['employerZip']}`}
                                                    ariaLabel={labelMapDisability['employerZip']}
                                                    onChange={(event): void => {
                                                        handleZIPInput('employerZip', event.target.value);
                                                    }}
                                                ></TSMaskedTextField>
                                                <SCErrorLabel>{employerZipInputError}</SCErrorLabel>
                                            </SCElementWrapper>
                                        </SCElementsWrapper>
                                        <SCFormNavigationWrapper>
                                            {contextStateClaimProgressDisability.some(
                                                (e) => e.status === TimelineState.ACTIVE
                                            ) && (
                                                <FormNavigation
                                                    formData={formData}
                                                    requiredFields={[
                                                        'employerName',
                                                        'employerContactNumber',
                                                        'employerAddress',
                                                        'employerCity',
                                                        'employerState',
                                                        'employerZip'
                                                    ]}
                                                    validationFields={[
                                                        !employerPhoneNumberInputError,
                                                        !employerZipInputError,
                                                        !employerEmailInputError
                                                    ]}
                                                />
                                            )}
                                        </SCFormNavigationWrapper>
                                    </form>
                                </SCFormWrapper>
                            </SCSectionWrapper>
                        )}
                        {isHealthcareInfoActive && (
                            <SCSectionWrapper>
                                <SCBodyLarge>
                                    Okay, we’ve got your employment information recorded. Next, please provide us with
                                    your healthcare provider details.
                                </SCBodyLarge>
                                <SCBodyLarge>All fields are required unless otherwise indicated.</SCBodyLarge>
                                <SCFormWrapper>
                                    <form
                                        id={'borrower'}
                                        onSubmit={(e: React.FormEvent<HTMLFormElement>): void =>
                                            handleSaveFormState(e, formData)
                                        }
                                        autoComplete={'on'}
                                    >
                                        <SCElementWrapper>
                                            <SCSectionSubHead>Healthcare Information</SCSectionSubHead>
                                            <SCBodyLarge>
                                                We may need to obtain information from your Healthcare Provider’s office
                                                to confirm information regarding your disability.
                                            </SCBodyLarge>
                                        </SCElementWrapper>
                                        <SCElementsWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.SINGLE}>
                                                <TSTextField
                                                    id={'disabilityHealthcareName'}
                                                    value={formData?.disabilityHealthcareName as string}
                                                    label={labelMapDisability['disabilityHealthcareName']}
                                                    placeholder={`Enter ${labelMapDisability['disabilityHealthcareName']}`}
                                                    ariaLabel={labelMapDisability['disabilityHealthcareName']}
                                                    maxLength={MaxFieldLengths.DISABILITY_HEALTHCARE_NAME}
                                                    onChange={handleForm}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.SINGLE}>
                                                <TSTextField
                                                    id={'disabilityHealthcareHospitalName'}
                                                    value={formData?.disabilityHealthcareHospitalName as string}
                                                    label={labelMapDisability['disabilityHealthcareHospitalName']}
                                                    placeholder={`Enter ${labelMapDisability['disabilityHealthcareHospitalName']}`}
                                                    ariaLabel={labelMapDisability['disabilityHealthcareHospitalName']}
                                                    maxLength={MaxFieldLengths.DISABILITY_HEALTHCARE_HOSPITAL_NAME}
                                                    onChange={handleForm}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.SINGLE}>
                                                <TSMaskedTextField
                                                    id={'disabilityHealthcareContactNumber'}
                                                    name={'phone'}
                                                    fieldType={'phone'}
                                                    value={formData?.disabilityHealthcareContactNumber as string}
                                                    label={labelMapDisability['disabilityHealthcareContactNumber']}
                                                    placeholder={`Enter ${labelMapDisability['disabilityHealthcareContactNumber']}`}
                                                    ariaLabel={labelMapDisability['disabilityHealthcareContactNumber']}
                                                    onChange={(event): void => {
                                                        handlePhoneNumberInput(
                                                            'disabilityHealthcareContactNumber',
                                                            event.target.value
                                                        );
                                                    }}
                                                ></TSMaskedTextField>
                                                <SCErrorLabel>{healthcarePhoneNumberInputError}</SCErrorLabel>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.TRIPLE}>
                                                <TSTextField
                                                    id={'disabilityHealthcareAddress'}
                                                    name={'address1'}
                                                    value={formData?.disabilityHealthcareAddress as string}
                                                    label={labelMapDisability['disabilityHealthcareAddress']}
                                                    placeholder={`Enter ${labelMapDisability['disabilityHealthcareAddress']}`}
                                                    ariaLabel={labelMapDisability['disabilityHealthcareAddress']}
                                                    maxLength={MaxFieldLengths.ADDRESS}
                                                    onChange={handleForm}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                        </SCElementsWrapper>
                                        <SCElementsWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.SINGLE}>
                                                <TSTextField
                                                    id={'disabilityHealthcareCity'}
                                                    name={'city'}
                                                    value={formData?.disabilityHealthcareCity as string}
                                                    label={labelMapDisability['disabilityHealthcareCity']}
                                                    placeholder={`Enter ${labelMapDisability['disabilityHealthcareCity']}`}
                                                    ariaLabel={labelMapDisability['disabilityHealthcareCity']}
                                                    maxLength={MaxFieldLengths.CITY}
                                                    onChange={handleForm}
                                                ></TSTextField>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.SINGLE}>
                                                <TSAutocomplete
                                                    id={'disabilityHealthcareState'}
                                                    value={formData?.disabilityHealthcareState}
                                                    label={labelMapDisability['disabilityHealthcareState']}
                                                    options={stateOptions}
                                                    getOptionLabel={(stateAbbreviation): string =>
                                                        stateOptionDict[stateAbbreviation]
                                                    }
                                                    onChange={(event, value): void => {
                                                        handleAutocompleteInput('disabilityHealthcareState', value);
                                                    }}
                                                ></TSAutocomplete>
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.SINGLE}>
                                                <TSMaskedTextField
                                                    id={'disabilityHealthcareZip'}
                                                    name={'zip'}
                                                    fieldType={'zipCode'}
                                                    value={formData?.disabilityHealthcareZip as string}
                                                    label={labelMapDisability['disabilityHealthcareZip']}
                                                    placeholder={`Enter ${labelMapDisability['disabilityHealthcareZip']}`}
                                                    ariaLabel={labelMapDisability['disabilityHealthcareZip']}
                                                    onChange={(event): void => {
                                                        handleZIPInput('disabilityHealthcareZip', event.target.value);
                                                    }}
                                                ></TSMaskedTextField>
                                                <SCErrorLabel>{healthcareZipInputError}</SCErrorLabel>
                                            </SCElementWrapper>
                                        </SCElementsWrapper>
                                        <SCFormNavigationWrapper>
                                            {contextStateClaimProgressDisability.some(
                                                (e) => e.status === TimelineState.ACTIVE
                                            ) && (
                                                <FormNavigation
                                                    formData={formData}
                                                    requiredFields={keysDisabilityDetailsHealthcare}
                                                    validationFields={[
                                                        !healthcarePhoneNumberInputError,
                                                        !healthcareZipInputError
                                                    ]}
                                                />
                                            )}
                                        </SCFormNavigationWrapper>
                                    </form>
                                </SCFormWrapper>
                            </SCSectionWrapper>
                        )}
                        {isCommentsActive && (
                            <SCSectionWrapper>
                                <SCBodyLarge>
                                    Almost done! We’ve got your healthcare details all recorded. If you have any
                                    additional comments or important notes, you can add them here before submitting your
                                    claim.{' '}
                                </SCBodyLarge>
                                <SCFormWrapper>
                                    <form
                                        id={'borrower'}
                                        onSubmit={(e: React.FormEvent<HTMLFormElement>): void =>
                                            handleSaveFormState(e, formData)
                                        }
                                        autoComplete={'on'}
                                    >
                                        <SCElementsWrapper>
                                            <SCSectionSubHead>Comments (optional)</SCSectionSubHead>
                                        </SCElementsWrapper>
                                        <SCElementsWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.TRIPLE}>
                                                <TSTextField
                                                    id={'borrowerComments'}
                                                    value={formData?.borrowerComments as string}
                                                    label={labelMapDisability['borrowerComments']}
                                                    placeholder={`Enter ${labelMapDisability['borrowerComments']}`}
                                                    ariaLabel={labelMapDisability['borrowerComments']}
                                                    maxLength={MaxFieldLengths.GENERAL_MAX}
                                                    multiline={true}
                                                    minRows={5}
                                                    onChange={(event: unknown): void => {
                                                        handleForm(event as React.FormEvent<HTMLInputElement>);
                                                    }}
                                                />
                                            </SCElementWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                <TSAutocomplete
                                                    id={'disabilityFiledOnBehalfOf'}
                                                    value={formData?.disabilityFiledOnBehalfOf}
                                                    label={labelMapDisability['disabilityFiledOnBehalfOf']}
                                                    options={yesNoAutocompleteOptionValues}
                                                    getOptionLabel={(yesNoValue): string =>
                                                        YesNoAutocompleteOptions[yesNoValue]
                                                    }
                                                    onChange={(event, value): void => {
                                                        handleAutocompleteInput('disabilityFiledOnBehalfOf', value, [
                                                            'submitterCompanyName',
                                                            'submitterNameFirst',
                                                            'submitterNameLast',
                                                            'submitterEmail',
                                                            'submitterPhone'
                                                        ]);
                                                    }}
                                                ></TSAutocomplete>
                                            </SCElementWrapper>
                                        </SCElementsWrapper>
                                        {formData && onBehalfOf && (
                                            <SCElementsWrapper>
                                                <SCElementWrapper widthAmount={FormFieldWidthEnum.TRIPLE}>
                                                    <TSTextField
                                                        id={'submitterCompanyName'}
                                                        value={formData?.submitterCompanyName as string}
                                                        label={`${labelMapDisability['submitterCompanyName']}${
                                                            contextStateDistributorData?.distributorKey === 'advantage'
                                                                ? ' (Optional)'
                                                                : ''
                                                        }`}
                                                        placeholder={`Enter ${labelMapDisability['submitterCompanyName']}`}
                                                        ariaLabel={labelMapDisability['submitterCompanyName']}
                                                        maxLength={MaxFieldLengths.COMPANY_NAME}
                                                        onChange={handleForm}
                                                    ></TSTextField>
                                                </SCElementWrapper>
                                                <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                    <TSTextField
                                                        id={'submitterNameFirst'}
                                                        value={formData?.submitterNameFirst as string}
                                                        label={`${labelMapDisability['submitterNameFirst']}`}
                                                        placeholder={`Enter ${labelMapDisability['submitterNameFirst']}`}
                                                        ariaLabel={labelMapDisability['submitterNameFirst']}
                                                        maxLength={MaxFieldLengths.FIRST_NAME}
                                                        onChange={handleForm}
                                                    ></TSTextField>
                                                </SCElementWrapper>
                                                <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                    <TSTextField
                                                        id={'submitterNameLast'}
                                                        value={formData?.submitterNameLast as string}
                                                        label={`${labelMapDisability['submitterNameLast']}`}
                                                        placeholder={`Enter ${labelMapDisability['submitterNameLast']}`}
                                                        ariaLabel={labelMapDisability['submitterNameLast']}
                                                        maxLength={MaxFieldLengths.LAST_NAME}
                                                        onChange={handleForm}
                                                    ></TSTextField>
                                                </SCElementWrapper>
                                                <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                    <TSTextField
                                                        id={'submitterEmail'}
                                                        type={'email'}
                                                        value={formData?.submitterEmail as string}
                                                        label={labelMapDisability['submitterEmail']}
                                                        placeholder={`Enter ${labelMapDisability['submitterEmail']}`}
                                                        ariaLabel={labelMapDisability['submitterEmail']}
                                                        maxLength={MaxFieldLengths.EMAIL}
                                                        onChange={(
                                                            formEvent: React.FormEvent<HTMLInputElement>
                                                        ): void => {
                                                            handleEmailInput(
                                                                'submitterEmail',
                                                                formEvent.currentTarget.value
                                                            );
                                                        }}
                                                    ></TSTextField>
                                                    <SCErrorLabel>{submitterEmailInputError}</SCErrorLabel>
                                                </SCElementWrapper>
                                                <SCElementWrapper widthAmount={FormFieldWidthEnum.DOUBLE}>
                                                    <TSMaskedTextField
                                                        id={'submitterPhone'}
                                                        fieldType={'phone'}
                                                        value={formData?.submitterPhone as string}
                                                        label={labelMapDisability['submitterPhone']}
                                                        placeholder={`Enter ${labelMapDisability['submitterPhone']}`}
                                                        ariaLabel={labelMapDisability['submitterPhone']}
                                                        onChange={(event): void => {
                                                            handlePhoneNumberInput(
                                                                'submitterPhone',
                                                                event.target.value
                                                            );
                                                        }}
                                                    ></TSMaskedTextField>
                                                    <SCErrorLabel>{submitterPhoneNumberInputError}</SCErrorLabel>
                                                </SCElementWrapper>
                                            </SCElementsWrapper>
                                        )}
                                        <SCFormNavigationWrapper>
                                            {contextStateClaimProgressDisability.some(
                                                (e) => e.status === TimelineState.ACTIVE
                                            ) && (
                                                <FormNavigation
                                                    formData={formData}
                                                    requiredFields={
                                                        formData?.disabilityFiledOnBehalfOf === 'yes'
                                                            ? [
                                                                  'submitterNameFirst',
                                                                  'submitterNameLast',
                                                                  'submitterEmail',
                                                                  'submitterPhone',
                                                                  ...(contextStateDistributorData?.distributorKey !==
                                                                  'advantage'
                                                                      ? ['submitterCompanyName']
                                                                      : [])
                                                              ]
                                                            : []
                                                    }
                                                    validationFields={[
                                                        !submitterEmailInputError,
                                                        !submitterPhoneNumberInputError
                                                    ]}
                                                />
                                            )}
                                        </SCFormNavigationWrapper>
                                    </form>
                                </SCFormWrapper>
                            </SCSectionWrapper>
                        )}
                        {isClaimPaymentDetailsActive && contextStateDistributorData?.distributorKey === 'advantage' && (
                            <SCSectionWrapper>
                                <SCBodyLarge>
                                    One last step, in the event that claim benefits will be extended on your claim,
                                    please let us know you would like to receive the payment.
                                </SCBodyLarge>
                                <SCFormWrapper>
                                    <form
                                        id={'borrower'}
                                        onSubmit={(e: React.FormEvent<HTMLFormElement>): void =>
                                            handleSaveFormState(e, formData)
                                        }
                                        autoComplete={'on'}
                                    >
                                        <SCElementsWrapper>
                                            <SCElementWrapper widthAmount={FormFieldWidthEnum.TRIPLE}>
                                                <TSRadioGroup
                                                    id={'disabilityClaimPaymentDetails'}
                                                    value={formData?.disabilityClaimPaymentDetails}
                                                    radioGroupLabel={
                                                        labelMapDisability['disabilityClaimPaymentDetails']
                                                    }
                                                    radioGroupValues={borrowerPaymentOptions}
                                                    onChange={(event): void => {
                                                        handleRadioInput(
                                                            'disabilityClaimPaymentDetails',
                                                            event.target.value
                                                        );
                                                    }}
                                                    size={TSRadioSizeEnum.LARGE}
                                                    orientation={TSRadioOrientationEnum.VERTICAL}
                                                    color={$brightBlue}
                                                ></TSRadioGroup>
                                            </SCElementWrapper>
                                        </SCElementsWrapper>
                                        <SCFormNavigationWrapper>
                                            {contextStateClaimProgressDisability.some(
                                                (e) => e.status === TimelineState.ACTIVE
                                            ) && (
                                                <FormNavigation
                                                    formData={formData}
                                                    requiredFields={
                                                        formData?.disabilityFiledOnBehalfOf === 'yes'
                                                            ? ['submitterPhone']
                                                            : []
                                                    }
                                                    validationFields={[
                                                        !submitterEmailInputError,
                                                        !submitterPhoneNumberInputError
                                                    ]}
                                                />
                                            )}
                                        </SCFormNavigationWrapper>
                                    </form>
                                </SCFormWrapper>
                            </SCSectionWrapper>
                        )}
                    </SCFormRefWrapper>
                    <BeforeUnload
                        isLoggedIn={
                            contextStateAuthenticationState.isAuthenticated
                                ? contextStateAuthenticationState.isAuthenticated
                                : false
                        }
                        cancelHeaderButtonText={'Close'}
                        modalBody={
                            <SCContentSectionWrapper
                                flexDirection={FlexDirectionEnum.COLUMN}
                                flexAlignment={FlexAlignItemsEnum.FLEX_START}
                                flexGap={'24px'}
                            >
                                <SCSectionSubHead textColor={$digitalBlack}>Unsaved Changes</SCSectionSubHead>
                                <SCSectionBody>
                                    You have unsaved claim information, continuing may cause your information to be lost
                                </SCSectionBody>
                            </SCContentSectionWrapper>
                        }
                        nativeDialogue={true}
                        customDialogue={true}
                        modalEnabled={isFormDirty}
                        continueButtonText={'Proceed without saving'}
                        cancelButtonText={'Return to page'}
                        onContinue={(): void => {
                            setIsFormDirty(false);
                            updateContextStateClaimOption({ type: undefined, reviewState: ClaimStateEnum.ACTIVE });
                        }}
                        allowedRoutes={allowedRoutes}
                        onCancel={(): void => {}}
                    />
                    <TSDialog
                        ariaLabel={'Unsaved Changes dialog'}
                        hideSecondaryCloseButton
                        handleClose={(): void => {
                            setConfirmModalIsVisible(false);
                        }}
                        id={'unsavedChangesDialog'}
                        open={confirmModalIsVisible}
                        dialogSize={'sm'}
                        bannerHeaderCloseButtonText={'Close'}
                        content={
                            <SCContentSectionWrapper
                                flexDirection={FlexDirectionEnum.COLUMN}
                                flexAlignment={FlexAlignItemsEnum.FLEX_START}
                                flexGap={'24px'}
                            >
                                <SCSectionSubHead textColor={$digitalBlack}>Unsaved Changes</SCSectionSubHead>
                                <SCSectionBody>
                                    You have unsaved claim information, continuing may cause your information to be lost
                                </SCSectionBody>
                            </SCContentSectionWrapper>
                        }
                        actionChildren={
                            <SCContentSectionResponsiveWrapper
                                flexJustifyContent={FlexJustifyContentEnum.SPACE_AROUND}
                                flexAlignment={FlexAlignItemsEnum.CENTER}
                                flexDirection={FlexDirectionEnum.COLUMN}
                                flexGap={'16px'}
                            >
                                <TSButton
                                    ariaLabel="dialog return to page"
                                    tsVariant={TSButtonVariantsEnum.TEXT}
                                    size={TSButtonSizeEnum.MEDIUM}
                                    onClick={(): void => {
                                        setConfirmModalIsVisible(false);
                                    }}
                                >
                                    Return to page
                                </TSButton>
                                <TSButton
                                    ariaLabel="dialog proceed without saving"
                                    tsVariant={TSButtonVariantsEnum.ARROW}
                                    size={TSButtonSizeEnum.MEDIUM}
                                    onClick={(): void => {
                                        changeFormType && changeFormType();
                                        setConfirmModalIsVisible(false);
                                    }}
                                >
                                    Proceed without saving
                                </TSButton>
                            </SCContentSectionResponsiveWrapper>
                        }
                    ></TSDialog>
                </SCContentSectionResponsiveWrapper>
            </SCContentDirectionWrapper>
        </SCContentWrapper>
    );
};

export { FormDisability };
